import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  getAppDeviceId,
  readAppDeviceId,
  deleteAppDeviceId,
} from "../idempotency-key";

const Popup = ({ isOpen, onClose }) => {
  // Use a single ref object to store all form state that shouldn't cause re-renders
  const formState = useRef({
    started: false,
    lastFieldChanged: null,
    nameValue: "",
    emailValue: "",
    debugMessages: "",
    startedAt: null,
    renderedAt: null,
  });
  const formRef = useRef(null);

  // Only use state for values that should trigger UI updates
  const [formStarted, setFormStarted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
  });

  // Function to log debug messages without triggering re-renders
  const logDebug = useCallback((message) => {
    const timestamp = new Date().toISOString();
    formState.current.debugMessages += `\n${timestamp}: ${message}`;
    console.log(`DEBUG: ${message}`);
  }, []);

  // Function to handle form abandon submission
  const submitAbandonedForm = useCallback(() => {
    // Only submit if we have at least name or email and form wasn't successfully submitted
    if (
      !formState.current.started ||
      (!formState.current.nameValue && !formState.current.emailValue) ||
      isSubmitSuccessful
    ) {
      logDebug(
        "Abandoned form not submitted - no data or not started or already submitted"
      );
      return;
    }

    try {
      // Honeypot and timing checks for abandoned submission
      const minimumTypingTimeMs = 2000;
      const startedTime =
        formState.current.startedAt ||
        formState.current.renderedAt ||
        Date.now();
      const elapsedMs = Date.now() - startedTime;
      const companyWebsiteValue = (
        formRef.current?.elements?.companyWebsite?.value || ""
      ).trim();

      if (companyWebsiteValue || elapsedMs < minimumTypingTimeMs) {
        logDebug(
          `Abandon honeypot triggered. companyWebsiteFilled=${!!companyWebsiteValue}, elapsedMs=${elapsedMs}`
        );
        return;
      }

      // Create object with only name and email for existing endpoint
      const abandonData = {
        data: {
          name: formState.current.nameValue,
          email: formState.current.emailValue,
          form_status: "abandoned",
          pageURL: window.location.href,
          date: new Date().toUTCString(),
          website: window.location.origin,
          honeypot_elapsed_ms: elapsedMs,
        },
        sheetID: 122,
      };

      logDebug(`Sending abandoned form data: ${JSON.stringify(abandonData)}`);

      // Submission 1: Existing endpoint
      fetch("https://leads.civsav.com/template/contact", {
        method: "POST",
        body: JSON.stringify(abandonData),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        keepalive: true,
      }).catch((err) => {
        logDebug(`Fetch error (existing endpoint): ${err.message}`);
      });

      // Submission 2: Netlify function with idempotency key
      const netlifyPayload = {
        name: formState.current.nameValue,
        email: formState.current.emailValue,
        source_url: window.location.href,
        status: "abandoned",
        data: {
          pageURL: window.location.href,
          website: window.location.origin,
          date: new Date().toUTCString(),
        },
        formId: "6908be3e",
      };

      // Always pass idempotency key: read or create
      const idemKey =
        readAppDeviceId("get-popup-form") || getAppDeviceId("get-popup-form");

      logDebug(
        `Sending abandoned form via Netlify function: ${JSON.stringify(
          netlifyPayload
        )}`
      );

      fetch("/.netlify/functions/forms-api", {
        method: "POST",
        body: JSON.stringify(netlifyPayload),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          ...(idemKey ? { "Idempotency-Key": idemKey } : {}),
        },
        keepalive: true,
      }).catch((err) => {
        logDebug(`Fetch error (Netlify function): ${err.message}`);
      });

      // Track the abandoned form in analytics
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "form_abandon",
          form_type: "popup_contact_form",
          has_name: !!formState.current.nameValue,
          has_email: !!formState.current.emailValue,
          last_field_completed: formState.current.lastFieldChanged,
        });
        logDebug("GTM event pushed");
      }
    } catch (error) {
      logDebug(`Error in abandoned form: ${error.message}`);
    }
  }, [logDebug, isSubmitSuccessful]);

  // Enhanced input handler that tracks data for abandonment
  const trackFieldChange = useCallback(
    (name, value) => {
      // Store the data and update tracking state
      if (name === "name") {
        formState.current.nameValue = value;
      } else if (name === "email") {
        formState.current.emailValue = value;
      }

      formState.current.lastFieldChanged = name;

      // Mark form as started if not already
      if (!formState.current.started) {
        formState.current.started = true;
        formState.current.startedAt = Date.now();
        setFormStarted(true);
        logDebug(`Form started with ${name} field`);
      }

      logDebug(`${name} field updated: ${value}`);
    },
    [logDebug]
  );

  // Create an enhanced register function to track the field values
  const registerWithTracking = useCallback(
    (name, options) => {
      return {
        ...register(name, options),
        onChange: (e) => {
          trackFieldChange(name, e.target.value);
        },
        onInput: (e) => {
          trackFieldChange(name, e.target.value);
        },
        onAnimationStart: (e) => {
          if (e.animationName === "onAutoFillStart") {
            setTimeout(() => {
              trackFieldChange(name, e.target.value);
            }, 100);
          }
        },
      };
    },
    [register, trackFieldChange]
  );

  // Track when the popup becomes visible for timing-based honeypot
  useEffect(() => {
    if (isOpen) {
      formState.current.renderedAt = Date.now();
    }
  }, [isOpen]);

  const onSubmit = async (data) => {
    const toastId = toast.loading("Submitting...");
    logDebug(`Form submitted: ${JSON.stringify(data)}`);

    // Clear any pending abandon form submissions
    if (formState.current.abandonTimer) {
      clearTimeout(formState.current.abandonTimer);
      formState.current.abandonTimer = null;
      logDebug("Cleared abandon timer during form submission");
    }
    setFormStarted(false);

    // Honeypot checks: hidden field must be empty and minimum time must elapse
    const minimumTypingTimeMs = 2000;
    const startedTime =
      formState.current.startedAt || formState.current.renderedAt || Date.now();
    const elapsedMs = Date.now() - startedTime;
    const honeypotFilled = Boolean(data?.companyWebsite?.trim());

    if (honeypotFilled || elapsedMs < minimumTypingTimeMs) {
      // Silently treat as success to avoid tipping off bots
      reset();
      toast.success("Form submitted successfully!", { id: toastId });
      onClose();
      return;
    }

    const d = {
      data: {
        ...data,
        form_status: "completed",
        pageURL: window.location.href,
        date: new Date().toUTCString(),
        website: window.location.origin,
        honeypot_elapsed_ms: elapsedMs,
      },
      sheetID: 122,
    };

    // Send form data to both endpoints
    try {
      // Submission 1: Existing endpoint
      const response = await fetch(
        "https://leads.civsav.com/template/contact",
        {
          method: "POST",
          body: JSON.stringify(d),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Form submission failed");
      }

      await response.json();
      logDebug("Existing endpoint submission successful");

      // Submission 2: Netlify function with idempotency key
      const netlifyPayload = {
        ...data,
        source_url: window.location.href,
        status: "completed",
        data: {
          pageURL: window.location.href,
          website: window.location.origin,
          date: new Date().toUTCString(),
        },
        formId: "6908be3e",
      };

      // Always pass idempotency key: read or create
      const idemKey =
        readAppDeviceId("get-popup-form") || getAppDeviceId("get-popup-form");

      const netlifyResponse = await fetch("/.netlify/functions/forms-api", {
        method: "POST",
        body: JSON.stringify(netlifyPayload),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          ...(idemKey ? { "Idempotency-Key": idemKey } : {}),
        },
      });

      if (!netlifyResponse.ok) {
        logDebug("Netlify function submission failed");
      } else {
        logDebug("Netlify function submission successful");
        // Delete idempotency key on successful completed submission
        deleteAppDeviceId("get-popup-form");
      }

      // Track conversion
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "generate_lead",
        });
      }

      toast.success("Form submitted successfully!", { id: toastId });
      reset();
      onClose();
    } catch (error) {
      toast.error("Failed to submit form. Please try again.", { id: toastId });
      logDebug(`Form submission error: ${error.message}`);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle form abandonment only when closing the popup
  useEffect(() => {
    if (!isOpen && formStarted) {
      submitAbandonedForm();
    }
  }, [isOpen, formStarted, submitAbandonedForm]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl overflow-hidden mx-4 contact-four_content-box">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close popup"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-8">
          <div className="section-title text-center mb-[60px]">
            <h2>Get In Touch</h2>
            <p>
              We are here to help you. Please fill out the form below and we
              will get back to you as soon as possible.
            </p>
          </div>

          <div className="contact-form">
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              {/* Honeypot field: kept off-screen and should remain empty */}
              <div
                style={{
                  position: "absolute",
                  left: "-10000px",
                  top: "auto",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
                aria-hidden="true"
              >
                <label htmlFor="companyWebsite">Company Website</label>
                <input
                  type="text"
                  id="companyWebsite"
                  name="companyWebsite"
                  tabIndex={-1}
                  autoComplete="off"
                  {...register("companyWebsite")}
                />
              </div>
              <div className="form_group">
                <input
                  type="text"
                  className="form_control !border !border-solid !border-black placeholder:!text-black !text-black"
                  placeholder="Full Name"
                  {...registerWithTracking("name", {
                    required: "Name is required",
                  })}
                />
                {errors?.name && (
                  <span className="error-msg" style={{ marginTop: -15 }}>
                    {errors.name?.message}
                  </span>
                )}
              </div>

              <div className="form_group">
                <input
                  type="email"
                  className="form_control !border !border-solid !border-black placeholder:!text-black !text-black"
                  placeholder="Email Address"
                  {...registerWithTracking("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
                {errors?.email && (
                  <span className="error-msg" style={{ marginTop: -15 }}>
                    {errors.email?.message}
                  </span>
                )}
              </div>

              <div className="form_group">
                <input
                  type="tel"
                  className="form_control !border !border-solid !border-black placeholder:!text-black !text-black"
                  placeholder="Phone Number"
                  {...registerWithTracking("phone", {
                    required: "Phone number is required",
                    pattern: {
                      value: /^[0-9+-]+$/,
                      message: "Invalid phone number",
                    },
                  })}
                />
                {errors?.phone && (
                  <span className="error-msg" style={{ marginTop: -15 }}>
                    {errors.phone?.message}
                  </span>
                )}
              </div>

              <div className="form_group text-center">
                <button type="submit" className="main-btn btn-yellow">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Popup;
